import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';

import {GpHotTableColumn, HotTableDataType} from '../../+mass-data-update/mass-data-update.interfaces';
import {AnyData} from '../../shared/api';
import {LocalizationService} from '../../shared/localization/localization.service';
import {GPTranslateService} from '../../shared/localization/translate.service';


@Component({
  selector: 'gp-report-summary-row',
  templateUrl: './report-summary-row.component.html',
  styleUrls: ['./report-summary-row.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReportSummaryRowComponent implements OnChanges, OnDestroy {

  @Input() columns: GpHotTableColumn[] = [];

  @Input() data: Record<string, unknown>[] = [];

  @Input() hotInstance: AnyData = null;

  public columnSums: Record<string, number> = {};

  public hasSummary = false;

  private footerEl: HTMLElement | null = null;
  private fakeScrollbarEl: HTMLElement | null = null;
  private scrollSource: HTMLElement | null = null;

  private tableScrollListener: (() => void) | null = null;
  private fakeScrollListener: (() => void) | null = null;
  private afterRenderHook: (() => void) | null = null;

  private renderTimeoutId: any = null;
  private afterRenderRAF: number | null = null;
  private numericFields: string[] = [];
  private lastDataLength = -1;
  private lastColumnsLength = -1;

  constructor(
    private localization: LocalizationService,
    private gpTranslateService: GPTranslateService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['columns'] || changes['data'] || changes['hotInstance']) {
      this.recalculate();
    }
  }

  ngOnDestroy(): void {
    this.destroyOverlay();
    if (this.renderTimeoutId) {
      clearTimeout(this.renderTimeoutId);
      this.renderTimeoutId = null;
    }
    if (this.afterRenderRAF) {
      cancelAnimationFrame(this.afterRenderRAF);
      this.afterRenderRAF = null;
    }
  }


  get dataRowCount(): number {
    return this.data?.length ?? 0;
  }

  private recalculate(): void {
    if (!this.data?.length || !this.columns?.length || !this.hotInstance) {
      this.columnSums = {};
      this.hasSummary = false;
      this.destroyOverlay();
      return;
    }

    // Cache numeric fields list only when columns change
    if (this.columns.length !== this.lastColumnsLength) {
      this.lastColumnsLength = this.columns.length;
      this.numericFields = [];
      for (let i = 0; i < this.columns.length; i++) {
        const col = this.columns[i];
        if (col.type === HotTableDataType.NUMERIC || col.type === 'numeric') {
          const field = col.data as string;
          if (field) {
            this.numericFields.push(field);
          }
        }
      }
    }

    if (this.numericFields.length === 0) {
      this.columnSums = {};
      this.hasSummary = false;
      this.destroyOverlay();
      return;
    }

    this.columnSums = this.computeColumnSums();
    this.hasSummary = true;
    this.lastDataLength = this.data.length;

    if (this.renderTimeoutId) {
      clearTimeout(this.renderTimeoutId);
    }
    this.renderTimeoutId = setTimeout(() => {
      this.renderTimeoutId = null;
      this.renderOverlay();
      this.registerAfterRenderHook();
    });
  }

  private computeColumnSums(): Record<string, number> {
    const sums: Record<string, number> = {};
    const fields = this.numericFields;
    const data = this.data;
    const len = data.length;
    const fieldCount = fields.length;

    // Single pass over data for all numeric fields
    const totals = new Float64Array(fieldCount);
    for (let r = 0; r < len; r++) {
      const row = data[r];
      for (let f = 0; f < fieldCount; f++) {
        const raw = row[fields[f]];
        if (raw != null && raw !== '') {
          const value = +raw;
          if (value === value) { // faster isNaN check
            totals[f] += value;
          }
        }
      }
    }

    for (let f = 0; f < fieldCount; f++) {
      sums[fields[f]] = parseFloat(totals[f].toFixed(4));
    }

    return sums;
  }

  private renderOverlay(): void {
    const hot = this.hotInstance;
    if (!hot?.rootElement) {
      return;
    }

    const tableRoot: HTMLElement = hot.rootElement;
    const container: HTMLElement = tableRoot.parentElement;
    if (!container) {
      return;
    }

    container.style.position = 'relative';

    const masterWtHolder: HTMLElement = tableRoot.querySelector('.ht_master .wtHolder');
    const wtHider: HTMLElement = masterWtHolder?.querySelector('.wtHider');

    const isNewFooter = !this.footerEl;
    this.ensureFooterEl(container, isNewFooter);

    const visibleWidth = masterWtHolder?.clientWidth ?? 0;
    const totalScrollWidth = wtHider?.scrollWidth ?? visibleWidth;

    this.footerEl.style.width = `${visibleWidth}px`;

    const summaryTop = this.computeTop(tableRoot, masterWtHolder, wtHider);
    this.footerEl.style.top = `${summaryTop}px`;

    this.applyScrollbarFix(masterWtHolder);
    this.footerEl.innerHTML = this.buildHtml(hot, masterWtHolder);

    this.ensureFakeScrollbarEl(container);
    this.updateFakeScrollbarPosition(summaryTop, visibleWidth, totalScrollWidth);
    this.syncScroll(masterWtHolder, isNewFooter);
  }

  private ensureFooterEl(container: HTMLElement, isNew: boolean): void {
    if (!isNew) {
      return;
    }
    this.footerEl = document.createElement('div');
    this.footerEl.className = 'summary-row-footer';
    container.appendChild(this.footerEl);
  }

  private computeTop(tableRoot: HTMLElement, masterWtHolder: HTMLElement, wtHider: HTMLElement): number {
    const dataHeight = wtHider?.offsetHeight ?? 0;
    const holderOffsetTop = masterWtHolder?.offsetTop ?? 0;
    return Math.min(holderOffsetTop + dataHeight, tableRoot.offsetHeight);
  }

  private applyScrollbarFix(masterWtHolder: HTMLElement): void {
    if (!masterWtHolder) {
      return;
    }
    const scrollbarHeight = masterWtHolder.offsetHeight - masterWtHolder.clientHeight;
    masterWtHolder.style.overflowX = 'hidden';
    if (scrollbarHeight > 0) {
      masterWtHolder.style.height = `${masterWtHolder.offsetHeight + scrollbarHeight - 5}px`;
    }
  }

  private getColumnWidths(hot: AnyData, masterWtHolder: HTMLElement): number[] {
    const totalCols: number = hot.countCols();
    const colgroup: HTMLElement = masterWtHolder?.querySelector('.wtHider .wtSpreader table colgroup');
    const cols: NodeListOf<HTMLElement> = colgroup?.querySelectorAll('col');
    const hasRowHeaders = !!hot.getSettings().rowHeaders;
    const rowHeaderOffset: number = hasRowHeaders ? 1 : 0;

    const firstRenderedCol: number = hot.view?.wt?.wtTable?.getFirstRenderedColumn?.() ?? 0;

    const widths: number[] = new Array(totalCols);
    for (let c = 0; c < totalCols; c++) {
      const domIndex = c - firstRenderedCol + rowHeaderOffset;
      const col = cols && domIndex >= 0 && domIndex < cols.length ? cols[domIndex] : null;
      const domWidth = col ? parseInt(col.style.width, 10) : NaN;
      widths[c] = !isNaN(domWidth) && domWidth > 0 ? domWidth : (hot.getColWidth(c) ?? 100);
    }
    return widths;
  }

  private buildHtml(hot: AnyData, masterWtHolder: HTMLElement): string {
    const colWidths: number[] = this.getColumnWidths(hot, masterWtHolder);

    const columns: GpHotTableColumn[] = this.columns ?? [];
    const totalCols: number = hot.countCols();
    const translatedTotal: string = this.gpTranslateService.instant('COMMON.total');
    const columnSums = this.columnSums;

    const parts: string[] = new Array(totalCols + 2);
    parts[0] = '<div class="summary-row-footer-inner">';
    for (let c = 0; c < totalCols; c++) {
      const colWidth = colWidths[c];
      const col = columns[c];
      const field = col?.data as string;
      const hasSum = field && field in columnSums && columnSums[field] != null;

      let cellContent = '';
      if (hasSum) {
        const formatted = this.localization.localizeNumber(columnSums[field], 'DECIMAL_ALL_DIGITS');
        cellContent = `<span class="summary-value">${formatted}</span>`;
      } else if (c === totalCols - 1) {
        cellContent = `<span class="summary-label">${translatedTotal}</span>`;
      }

      parts[c + 1] = `<div class="summary-cell" style="width:${colWidth}px;min-width:${colWidth}px;max-width:${colWidth}px">${cellContent}</div>`;
    }
    parts[totalCols + 1] = '</div>';
    return parts.join('');
  }

  private ensureFakeScrollbarEl(container: HTMLElement): void {
    if (this.fakeScrollbarEl) {
      return;
    }
    this.fakeScrollbarEl = document.createElement('div');
    this.fakeScrollbarEl.className = 'summary-fake-scrollbar';
    const inner = document.createElement('div');
    inner.style.height = '1px';
    this.fakeScrollbarEl.appendChild(inner);
    container.appendChild(this.fakeScrollbarEl);
  }

  private updateFakeScrollbarPosition(top: number, visibleWidth: number, totalScrollWidth: number): void {
    const footerHeight = this.footerEl?.offsetHeight ?? 23;
    this.fakeScrollbarEl.style.top = `${top + footerHeight}px`;
    this.fakeScrollbarEl.style.width = `${visibleWidth}px`;
    (this.fakeScrollbarEl.firstChild as HTMLElement).style.width = `${totalScrollWidth}px`;
  }

  private syncScroll(masterWtHolder: HTMLElement, isNewSource: boolean): void {
    if (!masterWtHolder) {
      return;
    }

    const innerEl = this.footerEl?.querySelector('.summary-row-footer-inner') as HTMLElement;
    if (innerEl) {
      innerEl.style.transform = `translateX(-${masterWtHolder.scrollLeft}px)`;
    }
    if (this.fakeScrollbarEl) {
      this.fakeScrollbarEl.scrollLeft = masterWtHolder.scrollLeft;
    }

    if (!isNewSource && this.scrollSource === masterWtHolder) {
      return;
    }
    this.updateScrollListeners(masterWtHolder);
  }

  private updateScrollListeners(masterWtHolder: HTMLElement): void {
    if (this.tableScrollListener && this.scrollSource) {
      this.scrollSource.removeEventListener('scroll', this.tableScrollListener);
    }
    if (this.fakeScrollListener && this.fakeScrollbarEl) {
      this.fakeScrollbarEl.removeEventListener('scroll', this.fakeScrollListener);
    }

    this.scrollSource = masterWtHolder;

    this.tableScrollListener = () => {
      const inner = this.footerEl?.querySelector('.summary-row-footer-inner') as HTMLElement;
      if (inner) {
        inner.style.transform = `translateX(-${masterWtHolder.scrollLeft}px)`;
      }
      if (this.fakeScrollbarEl) {
        this.fakeScrollbarEl.scrollLeft = masterWtHolder.scrollLeft;
      }
    };
    masterWtHolder.addEventListener('scroll', this.tableScrollListener, {passive: true});

    this.fakeScrollListener = () => {
      if (masterWtHolder && this.fakeScrollbarEl) {
        masterWtHolder.scrollLeft = this.fakeScrollbarEl.scrollLeft;
      }
    };
    this.fakeScrollbarEl?.addEventListener('scroll', this.fakeScrollListener, {passive: true});
  }

  private registerAfterRenderHook(): void {
    if (!this.hotInstance) {
      return;
    }
    if (this.afterRenderHook) {
      this.hotInstance.removeHook('afterRender', this.afterRenderHook);
    }
    this.afterRenderHook = () => {
      if (this.afterRenderRAF) {
        cancelAnimationFrame(this.afterRenderRAF);
      }
      this.afterRenderRAF = requestAnimationFrame(() => {
        this.afterRenderRAF = null;
        this.renderOverlay();
      });
    };
    this.hotInstance.addHook('afterRender', this.afterRenderHook);
  }

  destroyOverlay(): void {
    if (this.afterRenderHook && this.hotInstance) {
      this.hotInstance.removeHook('afterRender', this.afterRenderHook);
      this.afterRenderHook = null;
    }

    if (this.afterRenderRAF) {
      cancelAnimationFrame(this.afterRenderRAF);
      this.afterRenderRAF = null;
    }

    if (this.tableScrollListener && this.scrollSource) {
      this.scrollSource.removeEventListener('scroll', this.tableScrollListener);
      this.tableScrollListener = null;
    }

    if (this.scrollSource) {
      this.scrollSource.style.overflowX = '';
      this.scrollSource.style.height = '';
      this.scrollSource = null;
    }

    if (this.fakeScrollListener && this.fakeScrollbarEl) {
      this.fakeScrollbarEl.removeEventListener('scroll', this.fakeScrollListener);
      this.fakeScrollListener = null;
    }

    if (this.fakeScrollbarEl) {
      this.fakeScrollbarEl.remove();
      this.fakeScrollbarEl = null;
    }

    if (this.footerEl) {
      this.footerEl.remove();
      this.footerEl = null;
    }
  }
}

